import React, { useState, useMemo } from 'react';
import { FileSignature, AlertTriangle, X, EyeOff, ArrowUpDown, Table2, LayoutGrid, Pin } from 'lucide-react';
import { useTranslation } from '../../i18n';
import { formatMoney } from '../../utils/format';
import { localize } from '../../data/i18nData';
import { estimateContractMargin } from '../../utils/contractMargin';
import useGameStore from '../../store/gameStore';
import { EMPTY_ARRAY } from '../../store/stableEmpties';
import { getLoyaltyTier } from '../../data/clientLoyalty';
import { resolveClient } from '../../utils/resolveClient';
import { getContractValidityDaysLeft, CONTRACT_BOARD_TTL_DAYS } from '../../store/helpers';
import ClientLogo from '../crm/ClientLogo';

const SORT_OPTION_KEYS = [
  { id: 'default',    labelKey: 'contracts_sort_default' },
  { id: 'margin',     labelKey: 'contracts_sort_margin' },
  { id: 'revenue',    labelKey: 'contracts_sort_revenue' },
  { id: 'category',   labelKey: 'contracts_sort_category' },
  { id: 'duration',   labelKey: 'contracts_sort_duration' },
];

/**
 * 03/06/2026 Phase 3 — Tableau de liège interactif.
 *
 * Deux modes d'affichage des contrats disponibles :
 *   - `table` : tableau compact dense (existant, AvailableContracts.jsx)
 *   - `cork`  : tableau de liège avec fiches "épinglées" — chaque carte
 *     ressemble à une fiche papier punaisée sur un fond liège. Animation
 *     d'arrivée + timer 14j visuel + ouverture détail au clic.
 *
 * Le toggle est sticky par session (state local). Le détail s'affiche
 * dans une modale plein écran (ContractDetailModal).
 */
export function CorkBoardContracts({
  availableContracts, drivers, vehicles, freeDrivers,
  marginUnlocked, canAcceptContract, onAccept,
  hasMarketIntelAccess, onOpenDetail,
}) {
  const { t } = useTranslation();
  const canSort = !!hasMarketIntelAccess;
  const [sortBy, setSortBy] = useState('default');

  const hiddenContractIds = useGameStore(s => s.hiddenContractIds || EMPTY_ARRAY);
  const hideAvailableContract = useGameStore(s => s.hideAvailableContract);
  const restoreHiddenContracts = useGameStore(s => s.restoreHiddenContracts);
  const totalDays = useGameStore(s => s.totalDays || 0);

  const visibleContracts = useMemo(() => (
    hiddenContractIds.length === 0
      ? availableContracts
      : availableContracts.filter(c => !hiddenContractIds.includes(c.instanceId))
  ), [availableContracts, hiddenContractIds]);

  const hiddenCount = availableContracts.length - visibleContracts.length;

  const sorted = useMemo(() => {
    // 04/06/2026 — Default sort = newest-first by spawnDay (same as the
    // table view). Anchors fresh arrivals at the top.
    const byNewest = (a, b) => {
      const dA = a.spawnDay ?? 0;
      const dB = b.spawnDay ?? 0;
      if (dA !== dB) return dB - dA;
      return (a.name || '').localeCompare(b.name || '');
    };
    if (!canSort || sortBy === 'default') {
      return [...visibleContracts].sort(byNewest);
    }
    const withScore = visibleContracts.map(c => {
      const m = estimateContractMargin(c, vehicles, drivers);
      const grossRevenue = c.frequency === 'ponctuel' ? (c.revenue || 0) : (c.dailyRevenue || 0) * (c.duration || 1);
      return { c, margin: m ? m.margin : -Infinity, marginPct: m ? m.marginPct : -Infinity, grossRevenue };
    });
    if (sortBy === 'margin')   withScore.sort((a, b) => b.margin - a.margin);
    if (sortBy === 'revenue')  withScore.sort((a, b) => b.grossRevenue - a.grossRevenue);
    if (sortBy === 'category') withScore.sort((a, b) => (a.c.category || '').localeCompare(b.c.category || ''));
    if (sortBy === 'duration') withScore.sort((a, b) => (b.c.duration || 0) - (a.c.duration || 0));
    return withScore.map(x => x.c);
  }, [visibleContracts, sortBy, canSort, vehicles, drivers]);

  return (
    <div className="rounded-md border border-amber-900/40 overflow-hidden" data-testid="cork-board-contracts">
      <div className="flex items-center justify-between px-4 py-3 border-b border-amber-900/40 bg-ink-800">
        <div className="flex items-center gap-2">
          <FileSignature className="w-3.5 h-3.5 text-amber-300" />
          <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase">
            {t('available_contracts_title')}
          </h3>
          <span className="font-mono text-[10px] text-slate-500 tabular">[{visibleContracts.length}]</span>
          {hiddenCount > 0 && (
            <button
              type="button"
              onClick={restoreHiddenContracts}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-ink-700/50 hover:bg-ink-700 text-slate-400 hover:text-slate-200 font-mono border border-ink-600 transition-colors"
              data-testid="restore-hidden-contracts-cork-btn"
            >
              <EyeOff className="w-3 h-3" />
              {t('contracts_restore_hidden_label', { count: hiddenCount })}
            </button>
          )}
        </div>
        {canSort && (
          <div className="flex items-center gap-1.5" data-testid="cork-sort-control">
            <ArrowUpDown className="w-3 h-3 text-slate-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="text-[10px] bg-ink-700 text-slate-200 border border-ink-600 rounded px-1.5 py-0.5 font-mono focus:outline-none focus:border-accent"
              data-testid="cork-sort-select"
            >
              {SORT_OPTION_KEYS.map(opt => (
                <option key={opt.id} value={opt.id}>{t(opt.labelKey)}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Cork board background */}
      <div className="cork-board p-5">
        {sorted.length === 0 ? (
          <div className="paper-note mx-auto max-w-md text-center py-6 px-4">
            <p className="text-sm font-handwriting text-stone-800">{t('no_contracts')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sorted.map((rawC) => {
              const c = localize('contract', rawC);
              const m = marginUnlocked ? estimateContractMargin(c, vehicles, drivers) : null;
              const neg = m && m.margin < 0;
              const accepted = canAcceptContract(c);
              const client = resolveClient(c);
              const daysOnBoard = totalDays - (c.spawnDay || 0);
              // 16/06/2026 — seuils alignés sur la demande utilisateur :
              // ambre ≤ 5 j, rouge ≤ 2 j (helper TTL partagé).
              const daysLeft = getContractValidityDaysLeft(c, totalDays) ?? CONTRACT_BOARD_TTL_DAYS;
              const isFresh = daysOnBoard <= 1;
              const isExpiring = daysLeft <= 2;
              return (
                <ContractPin
                  key={c.instanceId}
                  c={c}
                  client={client}
                  margin={m}
                  marginNeg={neg}
                  accepted={accepted}
                  daysLeft={daysLeft}
                  isFresh={isFresh}
                  isExpiring={isExpiring}
                  onAccept={() => onAccept(c)}
                  onHide={() => hideAvailableContract(c.instanceId)}
                  onOpenDetail={() => onOpenDetail && onOpenDetail(c)}
                  t={t}
                />
              );
            })}
          </div>
        )}

        {freeDrivers.length === 0 && drivers.length === 0 && (
          <div className="paper-note mx-auto max-w-md mt-5 px-4 py-3" data-testid="no-drivers-hint-cork">
            <p className="text-[11px] font-bold text-amber-900 flex items-center gap-1.5 mb-1">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
              {t('to_accept_contracts')}
            </p>
            <ol className="text-[11px] text-stone-800 ml-5 list-decimal space-y-0.5">
              <li>{t('go_to_hr_step')}</li>
              <li>{t('assign_driver_step')}</li>
              <li>{t('come_back_step')}</li>
            </ol>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * ContractPin — a single "paper note" card pinned to the cork board.
 */
function ContractPin({
  c, client, margin, marginNeg, accepted,
  daysLeft, isFresh, isExpiring,
  onAccept, onHide, onOpenDetail, t,
}) {
  // Stable rotation per contract id : -3°..+3° based on instanceId hash.
  const rot = useMemo(() => {
    let h = 0;
    for (const ch of (c.instanceId || '')) h = (h * 31 + ch.charCodeAt(0)) | 0;
    return ((h % 7) - 3); // -3..+3 degrees
  }, [c.instanceId]);

  return (
    <div
      className={`paper-note relative cursor-pointer group ${isFresh ? 'paper-note-fresh' : ''}`}
      style={{ transform: `rotate(${rot}deg)` }}
      data-testid={`cork-pin-${c.instanceId}`}
      onClick={onOpenDetail}
    >
      {/* Push pin */}
      <Pin className="w-5 h-5 absolute -top-2 left-1/2 -translate-x-1/2 text-rose-600 drop-shadow rotate-12 z-10 pin-shadow" />

      {/* Special / Loyalty tier ribbon */}
      {c.isSpecial && (
        <div className="absolute -top-1.5 -right-1.5 z-10">
          <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-500 text-amber-950 px-1.5 py-0.5 rounded shadow font-mono">
            {t('contracts_special_badge')}
          </span>
        </div>
      )}

      <div className="px-3 pt-4 pb-3">
        {/* Header — logo + name */}
        <div className="flex items-start gap-2.5 mb-2">
          <ClientLogo client={client} clientId={c.clientId} category={c.category} size="md" className="flex-shrink-0 shadow-md" />
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold text-stone-800 leading-tight line-clamp-2" title={c.name}>{c.name}</p>
            <p className="text-[10px] text-stone-700 italic truncate mt-0.5" title={c.clientName}>
              {c.clientName || '—'}
              {client && <span className="text-stone-600 ml-1">· {client.city}</span>}
            </p>
          </div>
          {/* Hide button */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onHide(); }}
            className="text-stone-500 hover:text-rose-600 transition-colors flex-shrink-0"
            data-testid={`cork-hide-${c.instanceId}`}
            title={t('contracts_hide_tooltip')}
            aria-label="Masquer"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Category + Tier badges */}
        <div className="flex items-center gap-1 flex-wrap mb-2">
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-600 text-white border border-sky-700 font-mono uppercase tracking-wider">
            {c.frequency === 'ponctuel' ? t('freq_ponctuel') : c.frequency === 'régulier' ? t('freq_regulier') : c.frequency}
          </span>
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-200 text-stone-800 font-mono uppercase tracking-wider">T{c.tierMin}+</span>
          <span className="text-[10px] text-stone-700 italic">· {c.category}</span>
        </div>

        {/* Price + duration */}
        <div className="flex items-baseline justify-between bg-amber-100/80 border border-amber-300/70 rounded px-2 py-1.5 mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Tarif</p>
            <p className="text-base font-mono font-black text-amber-900 leading-none">
              {(() => {
                const v = c.frequency === 'ponctuel' ? c.revenue : c.dailyRevenue;
                return (Number.isFinite(v) && v > 0) ? `${formatMoney(v)}` : '—';
              })()}
              <span className="text-[10px] font-normal text-amber-800 ml-0.5">
                {c.frequency === 'ponctuel' ? '' : (Number.isFinite(c.dailyRevenue) && c.dailyRevenue > 0 ? '/j' : '')}
              </span>
            </p>
          </div>
          {c.duration && (
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-800">Durée</p>
              <p className="text-sm font-mono font-bold text-amber-900 leading-none">{c.duration}j</p>
            </div>
          )}
        </div>

        {/* Margin row */}
        {margin && (
          <div className="flex items-center justify-between text-[10px] mb-2" data-testid={`cork-margin-${c.instanceId}`}>
            <span className="text-stone-700">Marge estimée</span>
            <span className={`font-mono font-bold ${marginNeg ? 'text-rose-700' : 'text-emerald-700'}`}>
              {formatMoney(margin.margin)} <span className="opacity-70">({margin.marginPct}%)</span>
            </span>
          </div>
        )}

        {/* Loyalty mini-tag */}
        <ClientLoyaltyChip clientId={c.clientId} />

        {/* Action bar */}
        <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-amber-300/60">
          <ExpirationGauge daysLeft={daysLeft} isExpiring={isExpiring} t={t} />
          <button
            onClick={(e) => { e.stopPropagation(); onAccept(); }}
            disabled={!accepted}
            className={`text-[10px] px-2.5 py-1 rounded-md font-mono font-bold uppercase tracking-wider transition-all flex-shrink-0 ${
              !accepted
                ? 'bg-stone-300 text-stone-500 cursor-not-allowed'
                : c.isSpecial
                  ? 'bg-amber-500 text-amber-950 hover:bg-amber-400 shadow-md'
                  : 'bg-sky-600 text-white hover:bg-sky-500 shadow-md'
            }`}
            data-testid={`cork-accept-${c.instanceId}`}
          >
            {t('accept')}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * ExpirationGauge — small horizontal timer showing days left before the
 * contract leaves the market board (14-day TTL).
 */
function ExpirationGauge({ daysLeft, isExpiring, t }) {
  const pct = Math.max(0, Math.min(100, (daysLeft / CONTRACT_BOARD_TTL_DAYS) * 100));
  // 16/06/2026 — couleurs alignées : vert > 5 j, ambre ≤ 5 j, rouge ≤ 2 j.
  const color = isExpiring ? '#dc2626' : daysLeft <= 5 ? '#d97706' : '#16a34a';
  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0">
      <div className="flex-1 h-1.5 bg-amber-100/60 rounded-full overflow-hidden">
        <div
          className="h-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-[10px] font-mono font-bold whitespace-nowrap" style={{ color }}>
        {t('contracts_validity_badge', { days: daysLeft })}
      </span>
    </div>
  );
}

function ClientLoyaltyChip({ clientId }) {
  const score = useGameStore(s => s.clientLoyalty?.[clientId]?.score);
  if (!clientId || score == null) return null;
  const tier = getLoyaltyTier(score);
  if (!tier) return null;
  return (
    <div
      className="text-[10px] inline-flex items-center gap-1 font-mono font-bold uppercase tracking-wider mb-1 px-1.5 py-0.5 rounded"
      style={{ backgroundColor: `${tier.color}22`, border: `1px solid ${tier.color}66` }}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: tier.color }}
      />
      <span style={{ color: tier.color, filter: 'brightness(0.7)' }}>{tier.label}</span>
      <span className="text-stone-700">{Math.round(score)}/100</span>
    </div>
  );
}
